import {
  Component,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  OnInit,
} from "@angular/core";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ViewRangeSliderComponent } from "src/app/core/view/view-range-slider/view-range-slider.component";
import { SearchState } from "../search/search.state";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";

interface RangeValue {
  lower: number | null;
  upper: number | null;
}

/**
 * Custom Formly field type for range slider (e.g., employee count).
 * Wraps the existing ViewRangeSliderComponent.
 * Listens to SearchState.resetFilters$ to reset the slider.
 *
 * Usage:
 * ```typescript
 * {
 *   key: 'EmployeeRange',
 *   type: 'range-slider',
 *   props: {
 *     label: 'Amount employees',
 *     min: 0,
 *     max: 10000
 *   }
 * }
 * ```
 *
 * Note: This field outputs { lower, upper } but the search param builder
 * should split this into NumberOfEmployeesLower/Upper for the API.
 */
@Component({
  selector: "formly-range-slider",
  standalone: true,
  imports: [TranslateModule, ViewRangeSliderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <app-view-range-slider
      [title]="(props.label || '') | translate"
      [minValue]="props.min || 0"
      [maxValue]="props.max || 10000"
      [resetListener]="resetSubject"
      (valuesOutput)="onRangeChange($event)"
    ></app-view-range-slider>
  `,
})
export class RangeSliderFieldType
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchState = inject(SearchState, { optional: true });

  // Subject to trigger reset in the child component
  readonly resetSubject = new Subject<void>();

  ngOnInit(): void {
    // Listen for reset events from SearchState
    this.searchState?.resetFilters$
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formControl.reset();
        this.resetSubject.next();
      });
  }

  onRangeChange(event: { lowerValue: number; upperValue: number }): void {
    const value: RangeValue = {
      lower: event.lowerValue,
      upper: event.upperValue,
    };
    this.formControl.setValue(value);
  }
}
